import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_CORPORATE_CHALLENGES } from "../../../base_routes_paths/corporate_challenges_paths";

const PRIMARY = "#4f005f";
const CONTEST_STARTED_KEY = "leaderboard_contest_started";

const SectionCard = ({ children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "#fafafa",
    }}
  >
    {children}
  </Paper>
);

const Register = () => {
  const { token } = AUTH_CONTEXT();

  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    corporateID: "",
  });
  const [inputMode, setInputMode] = useState("email"); // "email" | "phone"
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [contestStarted, setContestStarted] = useState(
    () => localStorage.getItem(CONTEST_STARTED_KEY) === "true"
  );

  // Challenge config state
  const [challengeConfig, setChallengeConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  useEffect(() => {
    const fetchChallengeConfig = async () => {
      setIsConfigLoading(true);
      try {
        const response = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCompetetiveChallengeConfig`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        if (response.status === 200 && data.status === 200) {
          setChallengeConfig(data.data);
        } else {
          toast.error(data.error || "Failed to load challenge config", {
            position: "bottom-left",
            autoClose: 3000,
            hideProgressBar: true,
          }); 
        }
      } catch (error) {
        toast.error(error.message, {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsConfigLoading(false);
      }
    };

    fetchChallengeConfig();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailBlur = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneChange = (e) => {
    // Strip anything that isn't a digit or leading +
    const raw = e.target.value;
    const cleaned = raw.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
    setFormData((prev) => ({ ...prev, phoneNumber: cleaned }));
  };

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{7,15}$/;

    if (inputMode === "email") {
      if (!formData.email.trim()) {
        toast.error("Please enter an email address");
        return false;
      }
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return false;
      }
    } else {
      if (!formData.phoneNumber.trim()) {
        toast.error("Please enter a phone number");
        return false;
      }
      if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ""))) {
        toast.error("Please enter a valid phone number");
        return false;
      }
    }

    if (!formData.corporateID) {
      toast.error("Please select a corporate ID");
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const hasPhone = inputMode === "phone";

    setIsLoading(true);
    try {
      const response = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminRegisterUserToCompetetiveChallenge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Mobile_Number: hasPhone ? Number(formData.phoneNumber.trim()) : -1,
            Email: hasPhone ? "" : formData.email.trim(),
            CorporateID: formData.corporateID,
          }),
        }
      );

      const data = await response.json();

      if (response.status === 200 && data.status === 200 && data.data?.message === true) {
        toast.success("User registered successfully!", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "light",
        });
        setFormData({ email: "", phoneNumber: "", corporateID: "" });
      } else {
        toast.error(data.error || "Registration failed", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartContest = async () => {
    setConfirmOpen(false);
    setIsStarting(true);
    try {
      const response = await fetch(
        `${ARENO_API_ENDPOINT}/leaderboard/AdminStartContest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.status === 200 && data.status === 200) {
        localStorage.setItem(CONTEST_STARTED_KEY, "true");
        setContestStarted(true);
        toast.success("Contest started successfully!", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "light",
        });
      } else {
        toast.error(data.error || "Failed to start contest", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_HOME}>
          Dashboard
        </Link>
        <Link underline="hover" color="inherit">
          Corporate Challenges
        </Link>
        <Typography color="text.primary" fontWeight={500}>
          Register
        </Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PersonAddAltIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2} color="#181818">
            Arm Wrestling Sign-Up
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the details below to register a user for the Arm Wrestling Challenge
          </Typography>
        </Box>
      </Stack>

      {/* Points Per Match info */}
      <Box mb={3}>
        {isConfigLoading ? (
          <Skeleton variant="rounded" width={200} height={32} />
        ) : challengeConfig ? (
          <Chip
            icon={<StarRoundedIcon sx={{ color: "#FFD700 !important", fontSize: 18 }} />}
            label={`${challengeConfig.PointsPerMatch} points per match`}
            sx={{
              fontWeight: 700,
              fontSize: "0.875rem",
              backgroundColor: PRIMARY,
              color: "#fff",
              px: 0.5,
              height: 34,
            }}
          />
        ) : null}
      </Box>

      <form onSubmit={handleRegister}>
        <Stack spacing={3}>
          {/* Contact — toggle between email and phone */}
          <SectionCard>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Contact
            </Typography>

            {/* Toggle */}
            <Stack direction="row" spacing={0} mb={2.5}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                width: "fit-content",
              }}
            >
              {[
                { value: "email", label: "Email" },
                { value: "phone", label: "Phone Number" },
              ].map(({ value, label }) => (
                <Button
                  key={value}
                  disableElevation
                  onClick={() => {
                    setInputMode(value);
                    setEmailError("");
                    setFormData((prev) => ({ ...prev, email: "", phoneNumber: "" }));
                  }}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 0,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    backgroundColor: inputMode === value ? PRIMARY : "transparent",
                    color: inputMode === value ? "#fff" : "text.secondary",
                    "&:hover": {
                      backgroundColor: inputMode === value ? PRIMARY : `${PRIMARY}10`,
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {label}
                </Button>
              ))}
            </Stack>

            {/* Active field */}
            {inputMode === "email" ? (
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                error={!!emailError}
                helperText={emailError}
                placeholder="e.g. user@example.com"
              />
            ) : (
              <TextField
                fullWidth
                name="phoneNumber"
                label="Phone Number"
                type="tel"
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                placeholder="e.g. 9876543210"
                inputProps={{ inputMode: "numeric" }}
              />
            )}
          </SectionCard>

          {/* Corporate ID */}
          <SectionCard>
            <FormControl component="fieldset" fullWidth>
              <FormLabel
                component="legend"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 1.5,
                  fontSize: "1rem",
                  "&.Mui-focused": { color: "text.primary" },
                }}
              >
                Corporate ID
              </FormLabel>
              {isConfigLoading ? (
                <Stack direction="row" spacing={2}>
                  <Skeleton variant="rounded" width="100%" height={72} />
                  <Skeleton variant="rounded" width="100%" height={72} />
                </Stack>
              ) : (
                <RadioGroup
                  name="corporateID"
                  value={formData.corporateID}
                  onChange={handleChange}
                  row
                  sx={{ gap: 2 }}
                >
                  {(challengeConfig?.CorporateIDs ?? []).map((id) => (
                    <Paper
                      key={id}
                      elevation={0}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, corporateID: id }))
                      }
                      sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: 2,
                        border: "2px solid",
                        borderColor:
                          formData.corporateID === id ? PRIMARY : "divider",
                        backgroundColor:
                          formData.corporateID === id
                            ? `${PRIMARY}0d`
                            : "#fff",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: PRIMARY,
                          backgroundColor: `${PRIMARY}08`,
                        },
                      }}
                    >
                      <FormControlLabel
                        value={id}
                        control={
                          <Radio
                            sx={{
                              color: PRIMARY,
                              "&.Mui-checked": { color: PRIMARY },
                              p: 0,
                              mr: 1,
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color={
                              formData.corporateID === id
                                ? PRIMARY
                                : "text.primary"
                            }
                            lineHeight={1}
                          >
                            {id}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          alignItems: "center",
                          "& .MuiFormControlLabel-label": {
                            color: formData.corporateID === id ? PRIMARY : "#181818",
                          },
                          "&:hover .MuiFormControlLabel-label": {
                            color: PRIMARY,
                          },
                        }}
                      />
                    </Paper>
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          </SectionCard>

          <Divider />

          {/* Register Button */}
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setFormData({ email: "", phoneNumber: "", corporateID: "" });
                setEmailError("");
                setInputMode("email");
              }}
              disabled={isLoading}
              sx={{ px: 4, py: 1.5 }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                px: 4,
                py: 1.5,
                backgroundColor: PRIMARY,
                color: "#fff",
                "&:hover": { backgroundColor: "#3a0047" },
              }}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PersonAddAltIcon fontSize="small" />
                )
              }
            >
              {isLoading ? "Registering..." : "Register"}
            </Button>
          </Stack>
        </Stack>
      </form>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Start Contest?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to start the contest? This action cannot be undone and the
            competition will begin immediately for all registered participants.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setConfirmOpen(false)}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStartContest}
            sx={{
              px: 3,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: "#3a0047" },
            }}
            startIcon={<PlayArrowRoundedIcon />}
          >
            Yes, Start Contest
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Register;
